import React, { useCallback } from "react";
import { toast } from "react-toastify";
import InfoTooltip from "../common/InfoTooltip";

const VariantTable = ({ 
  variants, 
  onVariantsChange, 
  attributes, 
  productName, 
  inputClass,
  unitOptions = [],       // <-- Receive unitOptions as prop
  variantErrors = {}      // ✅ NEW — { [variantId]: { sku: true, mrp: true, unit: true } }
}) => {
  const updateVariant = useCallback((id, field, value) => {
    onVariantsChange(variants.map(v => (v.id === id ? { ...v, [field]: value } : v)));
  }, [variants, onVariantsChange]);

  const copyToAllField = useCallback((field, label) => {
    const firstVariant = variants.find(v => v[field] !== '' && v[field] != null);
    if (!firstVariant) {
      toast.warning(`Please enter a ${label} value first`);
      return;
    }
    onVariantsChange(variants.map(v => ({ ...v, [field]: firstVariant[field] })));
    toast.success(`${label} copied to all variants`);
  }, [variants, onVariantsChange]);

  const copyUnitToAll = useCallback(() => {
    const firstVariant = variants.find(v => v.unit && v.unit !== '');
    if (!firstVariant) {
      toast.warning("Please select a Pack Unit value first");
      return;
    }
    onVariantsChange(variants.map(v => ({ ...v, unit: firstVariant.unit })));
    toast.success("Pack Unit copied to all variants");
  }, [variants, onVariantsChange]);

  // const copyExpiryToAll = useCallback(() => {
  //   if (variants.length === 0) return;
  //   const value = !!variants[0].isExpiryTracked;
  //   onVariantsChange(variants.map(v => ({ ...v, isExpiryTracked: value })));
  //   toast.success('Expiry tracking copied to all variants');
  // }, [variants, onVariantsChange]);

  const generateAllSkus = useCallback(() => {
    onVariantsChange(variants.map((v, index) => ({
      ...v,
      sku: `${productName.substring(0,3).toUpperCase()}-${String(index + 1).padStart(3, '0')}`
    })));
    toast.success('SKUs generated for all variants');
  }, [variants, onVariantsChange, productName]);

  const generateAllBarcodes = useCallback(() => {
    onVariantsChange(variants.map((v, index) => ({
      ...v,
      barcode: `BAR-${String(index + 1).padStart(6, '0')}`
    })));
    toast.success('Barcodes generated for all variants');
  }, [variants, onVariantsChange]);

  const variantAttrs = attributes.filter(a => a.is_variant_attribute);

  // ✅ Helper: red border/ring classes when a cell is invalid
  const errClass = (hasError) =>
    hasError ? "border-red-500 ring-2 ring-red-200" : "border-gray-300";

  if (variants.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-gray-500 text-sm">No variants generated</p>
        <p className="text-xs text-gray-400 mt-1">Add attributes with options to generate variants</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">Variants ({variants.length})</p>
        <p className="text-xs text-gray-400">Generated from {variantAttrs.map(a => a.name).join(' × ')}</p>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-[14%]">Item Name</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-[10%]">
                  <div className="flex items-center gap-1">
                    Pack Unit <span className="text-red-500">*</span>
                    {variants.length > 1 && (
                      <button 
                        onClick={copyUnitToAll} 
                        className="text-blue-600 text-xs hover:underline whitespace-nowrap"
                      >
                        Copy
                      </button>
                    )}
                    <InfoTooltip>
                      <p className="font-medium mb-1">What is Pack Unit?</p>
                      <p className="text-gray-300">
                        This defines how the product is sold or packed as a single unit.
                      </p>
                      <ul className="text-gray-300 mt-1 list-disc list-inside text-xs">
                        <li>Medicine: Strip, Vial, Bottle</li>
                        <li>Food: Pack, Box, Can, Bag</li>
                        <li>Toys: Piece, Set, Pack</li>
                        <li>Grooming: Bottle, Tube, Jar</li>
                      </ul>
                    </InfoTooltip>
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-[10%]">
                  <div className="flex items-center gap-1">
                    SKU <span className="text-red-500">*</span>
                    <button onClick={generateAllSkus} className="text-blue-600 text-xs hover:underline whitespace-nowrap">Generate</button>
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-[10%]">
                  <div className="flex items-center gap-1">
                    Barcode
                    <button onClick={generateAllBarcodes} className="text-blue-600 text-xs hover:underline whitespace-nowrap">Generate</button>
                  </div>
                </th>
                {/* <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-[9%]">
                  <div className="flex items-center gap-1">
                    Cost
                    {variants.length > 1 && (
                      <button onClick={() => copyToAllField('costPrice', 'Cost Price')} className="text-blue-600 text-xs hover:underline whitespace-nowrap">Copy</button>
                    )}
                  </div>
                </th> */}
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-[9%]">
                  <div className="flex items-center gap-1">
                    MRP <span className="text-red-500">*</span>
                    {variants.length > 1 && (
                      <button onClick={() => copyToAllField('mrp', 'MRP')} className="text-blue-600 text-xs hover:underline whitespace-nowrap">Copy</button>
                    )}
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-[9%]">
                  <div className="flex items-center gap-1">
                    Op. STOCK
                    {variants.length > 1 && (
                      <button onClick={() => copyToAllField('openingStock', 'Opening Stock')} className="text-blue-600 text-xs hover:underline whitespace-nowrap">Copy</button>
                    )}
                  </div>
                </th>
                {/* <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-[11%]">
                  <div className="flex items-center gap-1">
                    Restock Alert?
                    {variants.length > 1 && (
                      <button onClick={() => copyToAllField('restockAlert', 'Restock Alert')} className="text-blue-600 text-xs hover:underline whitespace-nowrap">Copy</button>
                    )}
                  </div>
                </th> */}
                {/* <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-[11%]">
                  <div className="flex items-center gap-1">
                    Expiry Tracked?
                    {variants.length > 1 && (
                      <button onClick={copyExpiryToAll} className="text-blue-600 text-xs hover:underline whitespace-nowrap">Copy</button>
                    )}
                  </div>
                </th> */}
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-[11%]">
                  Expiry Tracked?
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {variants.map((variant) => {
                // ✅ Look up this row's error flags (if any)
                const rowErrors = variantErrors[variant.id] || {};

                return (
                  <tr key={variant.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="text-xs text-gray-600 leading-tight line-clamp-2 break-words max-h-8 overflow-hidden">
                        {variant.itemName || ""}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={variant.unit || ''}
                        onChange={(e) => updateVariant(variant.id, 'unit', e.target.value)}
                        data-variant-error={rowErrors.unit ? "true" : undefined}
                        className={`${inputClass(false)} w-full text-xs min-w-[70px] px-2 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errClass(rowErrors.unit)}`}
                      >
                        <option value="">Select</option>
                        {unitOptions.map((u) => (
                          <option key={u} value={u}>{u.charAt(0).toUpperCase() + u.slice(1)}</option>
                        ))}
                      </select>
                      {rowErrors.unit && (
                        <p className="text-[10px] text-red-500 mt-0.5">Required</p>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input 
                        type="text" 
                        value={variant.sku || ''} 
                        onChange={(e) => updateVariant(variant.id, 'sku', e.target.value)}
                        placeholder="SKU" 
                        data-variant-error={rowErrors.sku ? "true" : undefined}
                        className={`${inputClass(false)} w-full text-xs min-w-[70px] px-2 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errClass(rowErrors.sku)}`} 
                      />
                      {rowErrors.sku && (
                        <p className="text-[10px] text-red-500 mt-0.5">Required</p>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input 
                        type="text" 
                        value={variant.barcode || ''} 
                        onChange={(e) => updateVariant(variant.id, 'barcode', e.target.value)}
                        placeholder="Barcode" 
                        className={`${inputClass(false)} w-full text-xs min-w-[70px] px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`} 
                      />
                    </td>
                    {/* <td className="px-3 py-2">
                      <input 
                        type="number" 
                        value={variant.costPrice || ''} 
                        onChange={(e) => updateVariant(variant.id, 'costPrice', e.target.value)}
                        placeholder="0" 
                        step="0.01" 
                        min="0" 
                        className={`${inputClass(false)} w-full text-xs min-w-[60px] px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`} 
                      />
                    </td> */}
                    <td className="px-3 py-2">
                      <input 
                        type="number" 
                        value={variant.mrp || ''} 
                        onChange={(e) => updateVariant(variant.id, 'mrp', e.target.value)}
                        placeholder="0" 
                        step="0.01" 
                        min="0" 
                        data-variant-error={rowErrors.mrp ? "true" : undefined}
                        className={`${inputClass(false)} w-full text-xs min-w-[60px] px-2 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errClass(rowErrors.mrp)}`} 
                      />
                      {rowErrors.mrp && (
                        <p className="text-[10px] text-red-500 mt-0.5">Required</p>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input 
                        type="number" 
                        value={variant.openingStock || ''} 
                        onChange={(e) => updateVariant(variant.id, 'openingStock', e.target.value)}
                        placeholder="0" 
                        step="1" 
                        min="0" 
                        className={`${inputClass(false)} w-full text-xs min-w-[60px] px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`} 
                      />
                    </td>
                    {/* <td className="px-3 py-2">
                      <input 
                        type="number" 
                        value={variant.restockAlert || ''} 
                        onChange={(e) => updateVariant(variant.id, 'restockAlert', e.target.value)}
                        placeholder="e.g. 10" 
                        step="1" 
                        min="0" 
                        className={`${inputClass(false)} w-full text-xs min-w-[70px] px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`} 
                      />
                    </td> */}
                    <td className="px-3 py-2">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!variant.isExpiryTracked}
                          onChange={(e) => updateVariant(variant.id, 'isExpiryTracked', e.target.checked)}
                          className="w-4 h-4 accent-blue-600"
                        />
                        <span className="text-xs text-gray-600">{variant.isExpiryTracked ? 'FEFO' : 'FIFO'}</span>
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ✅ Summary banner if any row has errors */}
      {/* {Object.keys(variantErrors).length > 0 && (
        <p className="text-xs text-red-500">
          {Object.keys(variantErrors).length} variant{Object.keys(variantErrors).length > 1 ? 's' : ''} missing required fields (highlighted above). Please fill Pack Unit, SKU, and MRP for every variant before saving.
        </p>
      )} */}
    </div>
  );
};

export default VariantTable;